// Company switcher (top-left of the top bar, design spec 2026-10-19 §3.1; FOUNDER-OS 12): a 24 px
// company tile, the name and a chevron. The menu lists "★ Founder OS" (the founder's portfolio)
// first, then "My companies", then "Clients via {agency}" (`me.companies`, switcher-model.ts),
// each row with its health dot, unread count and ⌘-number; then the operator console for agency
// users (note §2: not a separate shell). ⌘⇧O / Ctrl+Shift+O opens it; ⌘1…⌘9 switch from anywhere,
// and 1–9 while it is open. A switch keeps the mode when the target company shows it.
import {
  Button,
  CompanyTile,
  CountPill,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Kbd,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useRouteContext } from "@tanstack/react-router";
import { CheckIcon, ChevronsUpDownIcon, ShieldCheckIcon, StarIcon } from "lucide-react";
import { useEffect, useRef } from "react";
import type { MeResponse } from "../api/types";
import { companiesOf, isOperator } from "../features/company";
import { meCompaniesQuery } from "../features/portfolio/portfolio-queries";
import { useCurrentModeKey } from "./rail-items";
import { useShellState } from "./shell-state";
import {
  companyLanding,
  HEALTH_LABEL,
  healthOf,
  menuDigit,
  type SwitcherRow,
  switcherDigit,
  switcherRows,
  switcherSections,
} from "./switcher-model";

/** True for ⌘⇧O (macOS) or Ctrl+Shift+O. */
export function isSwitcherShortcut(event: KeyboardEvent): boolean {
  return (
    (event.metaKey || event.ctrlKey) &&
    event.shiftKey &&
    !event.altKey &&
    (event.code === "KeyO" || event.key.toLowerCase() === "o")
  );
}

/** A modal (dialog, sheet, palette) owns the keyboard while it is open. */
function modalOpen(): boolean {
  return (
    document.querySelector('[role="dialog"][aria-modal="true"], [role="alertdialog"]') !== null
  );
}

const DOT: Record<ReturnType<typeof healthOf>, string> = {
  good: "bg-success",
  warn: "bg-warning",
  bad: "bg-destructive",
  none: "bg-border-strong",
};

/** The health dot (great/regular green, average amber, low red, none grey). */
export function HealthDot({ band, className }: { band: SwitcherRow["band"]; className?: string }) {
  const health = healthOf(band);
  return (
    <span
      role="img"
      aria-label={`Health: ${HEALTH_LABEL[health]}`}
      data-health={health}
      className={cn("size-2 shrink-0 rounded-full", DOT[health], className)}
    />
  );
}

/** The company's tile: Founder OS wears a star. */
export function SwitcherTile({ name, portfolio }: { name: string; portfolio: boolean }) {
  return portfolio ? (
    <CompanyTile name={name} className="bg-primary-soft text-primary-text">
      <StarIcon />
    </CompanyTile>
  ) : (
    <CompanyTile name={name} />
  );
}

export function CompanySwitcher({ me, current }: { me: MeResponse; current: string | null }) {
  const navigate = useNavigate();
  const { api } = useRouteContext({ from: "__root__" });
  const { switcherOpen, setSwitcherOpen, commandOpen } = useShellState();
  const modeKey = useCurrentModeKey();
  // The list with counts and health: read for a founder (their switcher is their map), and for
  // everyone else once they open the menu; `/v1/me` answers until then.
  const list = useQuery({ ...meCompaniesQuery(api), enabled: switcherOpen || !!me.portfolio });
  const rows = switcherRows(me, list.data);
  const sections = switcherSections(rows);
  const companies = companiesOf(me);

  const go = (row: SwitcherRow) => {
    const target = companies.find((c) => c.id === row.id);
    if (!target) return;
    setSwitcherOpen(false);
    const landing = companyLanding(me, target, modeKey);
    void navigate({ to: landing.to as never, params: landing.params as never });
  };
  const latest = useRef({ rows, go });
  latest.current = { rows, go };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isSwitcherShortcut(event)) {
        if (commandOpen || modalOpen()) return;
        event.preventDefault();
        setSwitcherOpen(true);
        return;
      }
      // ⌘1…⌘9 / Ctrl+1…9: the nth company, from anywhere but an input or a modal.
      const n = switcherDigit(event);
      if (n === null || commandOpen) return;
      const row = latest.current.rows.find((r) => r.digit === n);
      if (!row) return;
      event.preventDefault();
      latest.current.go(row);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [commandOpen, setSwitcherOpen]);

  const currentName =
    current === null
      ? "Operator console"
      : (companies.find((c) => c.slug === current)?.name ?? current);
  const currentRow = rows.find((r) => r.slug === current);

  return (
    <DropdownMenu open={switcherOpen} onOpenChange={setSwitcherOpen}>
      <DropdownMenuTrigger
        render={
          <Button
            variant="ghost"
            title={currentName}
            className="h-9 max-w-full min-w-0 shrink gap-2 px-1.5 font-semibold text-foreground pointer-coarse:h-11 md:max-w-64"
            aria-label={`Company: ${currentName}. Switch company`}
            aria-keyshortcuts="Meta+Shift+O Control+Shift+O"
          />
        }
      >
        {current === null ? (
          <CompanyTile name={currentName}>
            <ShieldCheckIcon />
          </CompanyTile>
        ) : (
          <SwitcherTile name={currentName} portfolio={currentRow?.kind === "portfolio"} />
        )}
        <span className="min-w-0 truncate" title={currentName}>
          {currentName}
        </span>
        <ChevronsUpDownIcon aria-hidden="true" className="text-faint max-sm:hidden" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-[min(70vh,560px)] min-w-72"
        data-slot="company-switcher"
        onKeyDown={(event) => {
          const n = menuDigit(event.nativeEvent);
          const row = n === null ? undefined : rows.find((r) => r.digit === n);
          if (!row) return;
          event.preventDefault();
          event.stopPropagation();
          go(row);
        }}
      >
        {sections.map((s, i) => (
          <DropdownMenuGroup key={s.key} data-section={s.key}>
            {i > 0 ? <DropdownMenuSeparator /> : null}
            <DropdownMenuLabel className="flex items-center gap-1.5">
              {s.key === "portfolio" ? (
                <StarIcon aria-hidden="true" className="size-3 fill-current text-primary-text" />
              ) : null}
              {s.label}
            </DropdownMenuLabel>
            {s.rows.map((r) => (
              <SwitcherItem key={r.id} row={r} current={r.slug === current} onPick={go} />
            ))}
          </DropdownMenuGroup>
        ))}
        {isOperator(me) ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => void navigate({ to: "/operator" })}>
              <CompanyTile name="Operator console">
                <ShieldCheckIcon />
              </CompanyTile>
              Operator console
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SwitcherItem({
  row,
  current,
  onPick,
}: {
  row: SwitcherRow;
  current: boolean;
  onPick: (row: SwitcherRow) => void;
}) {
  const health = healthOf(row.band);
  const extras = [
    row.band ? `health ${HEALTH_LABEL[health]}` : null,
    row.unread ? `${row.unread} unread` : null,
    row.approvals ? `${row.approvals} approvals waiting` : null,
  ].filter(Boolean);
  return (
    <DropdownMenuItem
      data-current={current || undefined}
      data-company={row.slug}
      aria-label={[row.name, ...extras].join(", ")}
      aria-keyshortcuts={row.digit ? `Meta+${row.digit} Control+${row.digit}` : undefined}
      onClick={() => onPick(row)}
    >
      <SwitcherTile name={row.name} portfolio={row.kind === "portfolio"} />
      <span className="min-w-0 flex-1 truncate">{row.name}</span>
      {row.kind === "portfolio" ? null : <HealthDot band={row.band} />}
      {row.unread > 0 ? <CountPill count={row.unread} tone="accent" /> : null}
      {current ? <CheckIcon aria-hidden="true" className="text-primary-text" /> : null}
      {row.digit ? (
        <Kbd aria-hidden="true" className="min-w-7">
          ⌘{row.digit}
        </Kbd>
      ) : null}
    </DropdownMenuItem>
  );
}
